import React, { createContext, useContext, useEffect, useState, ReactNode, useCallback, useRef } from 'react';
import { jwtDecode } from 'jwt-decode';
import { useQuery, useQueryClient } from 'react-query';
import { User, LoginCredentials, RegisterData } from '../types';
import api from '../services/apiClient';
import { authService, AUTH_TOKENS_EVENT, AuthTokensEventDetail, systemService } from '../services/api';
import { emitSessionExpiringSoon, emitSessionExpiring, emitSessionExtended } from '../utils/sessionEvents';
import { API_PATHS } from '../config/api';
import toast from 'react-hot-toast';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (credentials: LoginCredentials) => Promise<import('../types').AuthTokens>;
  register: (userData: RegisterData) => Promise<void>;
  logout: () => Promise<void>;
  extendSession: () => Promise<boolean>;
  refreshUser: () => Promise<void>;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

const DEFAULT_INACTIVITY_TIMEOUT_MS = 60 * 60 * 1000; // 60 minutos por defecto (aumentado para mejor UX)
const SESSION_WARNING_TIME_MS = 3 * 60 * 1000; // 3 minutos antes de expirar
const EMERGENCY_SAVE_TIME_MS = 5000; // 5 segundos para guardado de emergencia
const AUTO_EXTEND_THRESHOLD_MS = 15 * 60 * 1000; // 15 minutos antes de expirar (aumentado para mejor UX)

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const queryClient = useQueryClient();
  const tokenExpiryTimerRef = useRef<number | null>(null);
  const inactivityTimerRef = useRef<number | null>(null);
  const warningTimerRef = useRef<number | null>(null);
  const emergencySaveTimerRef = useRef<number | null>(null);
  const lastActivityRef = useRef<number>(Date.now());

  // Obtener configuración de tiempo de inactividad desde el sistema (endpoint público)
  const { data: inactivityTimeoutData } = useQuery(
    ['system-settings', 'session_inactivity_timeout_minutes'],
    () => systemService.getPublicSetting('session_inactivity_timeout_minutes'),
    {
      staleTime: 5 * 60 * 1000, // Cache por 5 minutos
      retry: false,
      select: (data) => data.value || 60, // Valor por defecto: 60 minutos
    }
  );

  // Calcular el timeout de inactividad desde la configuración
  const inactivityTimeoutMs = useCallback(() => {
    const minutes = inactivityTimeoutData || 60;
    
    if (typeof minutes !== 'number' || minutes <= 0) {
      return DEFAULT_INACTIVITY_TIMEOUT_MS;
    }
    
    return minutes * 60 * 1000; // Convertir minutos a milisegundos
  }, [inactivityTimeoutData]);

  const clearTimer = (timerRef: React.MutableRefObject<number | null>) => {
    if (timerRef.current) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  const clearAllTimers = useCallback(() => {
    clearTimer(tokenExpiryTimerRef);
    clearTimer(inactivityTimerRef);
    clearTimer(warningTimerRef);
    clearTimer(emergencySaveTimerRef);
  }, []);

  type LogoutReason = 'manual' | 'expired' | 'inactive';

  const performLogout = useCallback(
    async (reason: LogoutReason) => {
      // Limpiar todos los timers
      clearAllTimers();

      // Si es expiración automática, emitir evento para guardado de emergencia
      if (reason === 'expired' || reason === 'inactive') {
        emitSessionExpiring(reason, EMERGENCY_SAVE_TIME_MS);
        
        // Dar tiempo para guardado de emergencia antes de cerrar sesión
        await new Promise((resolve) => {
          emergencySaveTimerRef.current = window.setTimeout(() => {
            resolve(undefined);
          }, EMERGENCY_SAVE_TIME_MS);
        });
      }

      try {
        await authService.logout();
      } catch (error) {
        console.error('Error during logout:', error);
      } finally {
        // Limpiar el estado del usuario PRIMERO (esto es crítico)
        // Esto debe hacerse antes de cualquier otra cosa para que React Router
        // detecte inmediatamente que el usuario ya no está autenticado
        setUser(null);
        
        // Limpiar tokens del localStorage
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        
        // Limpiar todos los datos del cache de react-query
        // Esto asegura que no queden datos del usuario anterior
        queryClient.clear();
        
        // Mostrar mensaje según el tipo de logout
        if (reason === 'manual') {
          toast.success('Sesión cerrada exitosamente');
        } else if (reason === 'expired') {
          toast.error('Tu sesión ha expirado. Por favor inicia sesión nuevamente.');
        } else if (reason === 'inactive') {
          toast.error('Tu sesión se cerró por inactividad.');
        }
        
        // NO hacer navegación aquí - ProtectedRoute manejará la redirección automáticamente
        // cuando detecte que user === null. Esto es más limpio y sigue las mejores prácticas
        // de React Router al ser declarativo en lugar de imperativo.
      }
    },
    [clearAllTimers, queryClient]
  );

  const handleSessionExpiration = useCallback(
    (reason: Exclude<LogoutReason, 'manual'>) => {
      performLogout(reason).catch((error) => {
        console.error('Error handling session expiration:', error);
      });
    },
    [performLogout]
  );

  const decodeExpiration = (token: string | null) => {
    if (!token) return null;
    try {
      const decoded = jwtDecode<{ exp?: number }>(token);
      if (!decoded.exp) {
        return null;
      }
      return decoded.exp * 1000;
    } catch (error) {
      console.warn('No se pudo decodificar el token JWT:', error);
      return null;
    }
  };

  const extendSession = useCallback(async (): Promise<boolean> => {
    try {
      const refreshToken = localStorage.getItem('refresh_token');
      if (!refreshToken) return false;

      const response = await api.post(API_PATHS.AUTH_REFRESH, { refresh_token: refreshToken });
      const { access_token, refresh_token } = response.data;

      localStorage.setItem('access_token', access_token);
      localStorage.setItem('refresh_token', refresh_token);
      window.dispatchEvent(
        new CustomEvent<AuthTokensEventDetail>(AUTH_TOKENS_EVENT, {
          detail: { accessToken: access_token, refreshToken: refresh_token },
        })
      );
      const newExpiresAt = decodeExpiration(access_token);
      if (newExpiresAt) emitSessionExtended(newExpiresAt);
      return true;
    } catch (error) {
      console.error('Error extending session:', error);
      return false;
    }
  }, []);

  const scheduleTokenExpiryCheck = useCallback(
    (accessToken: string | null, refreshToken: string | null) => {
      clearTimer(tokenExpiryTimerRef);
      clearTimer(warningTimerRef);

      const expirations = [decodeExpiration(accessToken), decodeExpiration(refreshToken)].filter(
        (value): value is number => value !== null
      );

      if (expirations.length === 0) {
        return;
      }

      const nextExpiry = Math.min(...expirations) - Date.now();
      const expiresAt = Math.min(...expirations);

      if (nextExpiry <= 0) {
        handleSessionExpiration('expired');
        return;
      }

      // Programar advertencia 3 minutos antes de expirar
      if (nextExpiry > SESSION_WARNING_TIME_MS) {
        warningTimerRef.current = window.setTimeout(() => {
          emitSessionExpiringSoon(SESSION_WARNING_TIME_MS, expiresAt);
        }, nextExpiry - SESSION_WARNING_TIME_MS);
      } else {
        // Si ya estamos dentro del tiempo de advertencia, emitir inmediatamente
        emitSessionExpiringSoon(nextExpiry, expiresAt);
      }

      // Programar expiración real
      tokenExpiryTimerRef.current = window.setTimeout(() => {
        handleSessionExpiration('expired');
      }, nextExpiry);
    },
    [handleSessionExpiration]
  );

  const resetInactivityTimer = useCallback(() => {
    clearTimer(inactivityTimerRef);

    const accessToken = localStorage.getItem('access_token');
    if (!accessToken) return;

    const now = Date.now();
    const previousActivity = lastActivityRef.current;
    lastActivityRef.current = now;

    // Extender sesión automáticamente si el token expira pronto y la actividad es reciente (evitar extender en cada evento)
    const tokenExpiry = decodeExpiration(accessToken);
    if (tokenExpiry) {
      const timeUntilExpiry = tokenExpiry - now;
      const timeSincePreviousActivity = now - previousActivity;
      if (
        timeUntilExpiry > 0 &&
        timeUntilExpiry < AUTO_EXTEND_THRESHOLD_MS &&
        timeSincePreviousActivity > 30000
      ) {
        extendSession().catch((err) => {
          console.warn('No se pudo extender sesión automáticamente:', err);
        });
      }
    }

    const timeout = inactivityTimeoutMs();
    inactivityTimerRef.current = window.setTimeout(() => {
      handleSessionExpiration('inactive');
    }, timeout);
  }, [handleSessionExpiration, extendSession, inactivityTimeoutMs]);

  useEffect(() => {
    let cancelled = false;
    const initAuth = async () => {
      const token = localStorage.getItem('access_token');
      if (token) {
        try {
          const userData = await authService.getCurrentUser();
          if (!cancelled) {
            setUser(userData);
            scheduleTokenExpiryCheck(token, localStorage.getItem('refresh_token'));
            resetInactivityTimer();
          }
        } catch (error) {
          if (!cancelled) {
            localStorage.removeItem('access_token');
            localStorage.removeItem('refresh_token');
            queryClient.clear();
            setUser(null);
          }
        }
      } else {
        if (!cancelled) {
          queryClient.clear();
          setUser(null);
        }
      }
      if (!cancelled) setLoading(false);
    };

    initAuth();
    // Timeout de seguridad: si la API no responde (ej. dominio mal configurado), mostrar login tras 8s
    const safetyTimeout = window.setTimeout(() => {
      setLoading((prev) => (prev ? false : prev));
    }, 8000);
    return () => {
      cancelled = true;
      window.clearTimeout(safetyTimeout);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const activityEvents: (keyof WindowEventMap)[] = [
      'mousemove',
      'mousedown',
      'keydown',
      'touchstart',
      'scroll'
    ];

    const handleActivity = () => {
      resetInactivityTimer();
    };

    activityEvents.forEach((eventName) => {
      window.addEventListener(eventName, handleActivity, { passive: true });
    });

    return () => {
      activityEvents.forEach((eventName) => {
        window.removeEventListener(eventName, handleActivity);
      });
      clearTimer(inactivityTimerRef);
    };
  }, [resetInactivityTimer]);

  useEffect(() => {
    const handleTokenEvent = (event: CustomEvent<AuthTokensEventDetail>) => {
      const { accessToken, refreshToken } = event.detail;
      scheduleTokenExpiryCheck(accessToken, refreshToken);

      if (accessToken) {
        resetInactivityTimer();
      } else {
        clearTimer(inactivityTimerRef);
      }
    };

    window.addEventListener(AUTH_TOKENS_EVENT, handleTokenEvent);

    return () => {
      window.removeEventListener(AUTH_TOKENS_EVENT, handleTokenEvent);
    };
  }, [resetInactivityTimer, scheduleTokenExpiryCheck]);

  const refreshUser = useCallback(async () => {
    const token = localStorage.getItem('access_token');
    if (token) {
      try {
        const userData = await authService.getCurrentUser();
        setUser(userData);
      } catch (error) {
        console.warn('Error al refrescar usuario:', error);
      }
    }
  }, []);

  const login = async (credentials: LoginCredentials) => {
    try {
      // Limpiar el cache antes de hacer login para evitar datos del usuario anterior
      queryClient.clear();
      
      const tokens = await authService.login(credentials);
      const userData = await authService.getCurrentUser();
      setUser(userData);
      // No mostrar toast aquí, dejar que el componente que llama maneje el mensaje
      // para evitar mensajes duplicados y permitir mensajes personalizados (ej: reactivación)
      const accessToken = localStorage.getItem('access_token');
      const refreshToken = localStorage.getItem('refresh_token');
      scheduleTokenExpiryCheck(accessToken, refreshToken);
      resetInactivityTimer();
      return tokens;
    } catch (error: any) {
      // No mostrar toast aquí, dejar que el componente que llama maneje el error
      // para evitar mensajes duplicados
      throw error;
    }
  };

  const register = async (userData: RegisterData) => {
    try {
      await authService.register(userData);
      toast.success('Usuario registrado exitosamente');
    } catch (error: any) {
      // No mostrar toast aquí, dejar que el componente que llama maneje el error
      // para evitar mensajes duplicados
      throw error;
    }
  };

  const logout = async () => {
    await performLogout('manual');
  };

  const value: AuthContextType = {
    user,
    loading,
    login,
    register,
    logout,
    extendSession,
    refreshUser,
    isAuthenticated: !!user,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
